/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	ActionIcon,
	Box,
	Flex,
	Image,
	Skeleton,
} from '@mantine/core';

// Styles
import classes from './TableLastContributions.module.css';

// UI Local components
import { Button } from '../Button';
import { Text } from '../Text';
import { Title } from '../Title';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { QueueStatus } from '@config/constants/enum';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import {
	IconFileDownload,
	IconSend,
} from '@tabler/icons-react';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
export type TOffset = {
	brand: string;
	name: string;
	contributionDate: string;
	quantity: number;
	priceTTC: string;
	status: string;
};

type TTableLastContributionsProps = {
	data: TOffset[];
	onSeeMoreClick?: () => void;
	onDownload: (data: TOffset) => void;
	onSend: (data: TOffset) => void;
	isLoading: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const TableLastContributions = ({
	data,
	onSeeMoreClick,
	onDownload,
	onSend,
	isLoading,
}: TTableLastContributionsProps) => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();
	const { checkRole } = useAuthorization();

	/* ******************************** RENDERING ******************************* */

	if (isLoading)
		return (
			<Box className={classes.container}>
				<Box className={classes.header}>
					<Title
						order={6}
						c="var(--mantine-color-igluPrimary-9)"
					>
						{t('global.lastOffsets')}
					</Title>
					<Button
						onClick={onSeeMoreClick}
						variant="outline"
					>
						{t('global.viewMore')}
					</Button>
				</Box>
				<Box className={classes.body}>
					<table className={classes.tableStyle}>
						<thead>
							<tr>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.brand')}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.name')}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.contributionDate')}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.quantity')}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.totalPrice') +
											' ' +
											' (' +
											t('global.exclVat') +
											')'}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.actions')}
									</Text>
								</th>
							</tr>
						</thead>
						<tbody>
							{[...new Array(4)].map(
								(row: TOffset, index) => (
									<tr key={index}>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'160px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'160px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'130px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'60px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'60px'}
												radius="md"
											/>
										</td>
										<td
											className={classes.tdActionsStyle}
										>
											<Skeleton
												height={'20px'}
												width={'60px'}
												radius="md"
											/>
										</td>
									</tr>
								)
							)}
						</tbody>
					</table>
				</Box>
			</Box>
		);

	return (
		<Box className={classes.container}>
			<Box className={classes.header}>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{t('global.lastOffsets')}
				</Title>
				<Button
					onClick={onSeeMoreClick}
					variant="outline"
				>
					{t('global.viewMore')}
				</Button>
			</Box>
			<Box className={classes.body}>
				<table className={classes.tableStyle}>
					<thead>
						<tr>
							{!checkRole([
								ROLES.BRAND_ADMIN,
								ROLES.BRAND_STAFF,
							]) && (
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.brand')}
									</Text>
								</th>
							)}
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.name')}
								</Text>
							</th>
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.contributionDate')}
								</Text>
							</th>
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.quantity')}
								</Text>
							</th>
							{!checkRole([ROLES.STAFF]) && (
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.totalPrice') +
											' ' +
											' (' +
											t('global.exclVat') +
											')'}
									</Text>
								</th>
							)}
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.actions')}
								</Text>
							</th>
						</tr>
					</thead>
					<tbody>
						{Array.isArray(data) &&
							data.map((row: TOffset, index) => (
								<tr key={index}>
									{!checkRole([
										ROLES.BRAND_ADMIN,
										ROLES.BRAND_STAFF,
									]) && (
										<td className={classes.tdStyle}>
											<Text
												variant="subtitle1"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{row.brand}
											</Text>
										</td>
									)}
									<td className={classes.tdStyle}>
										<Text
											variant="body2"
											c="var(--mantine-color-igluTextPrimary-9)"
										>
											{row.name}
										</Text>
									</td>
									<td className={classes.tdStyle}>
										<Text
											variant="body2"
											c="var(--mantine-color-igluTextPrimary-9)"
										>
											{row.contributionDate}
										</Text>
									</td>
									<td className={classes.tdStyle}>
										<Text
											variant="body2"
											c="var(--mantine-color-igluTextPrimary-9)"
										>
											{row.quantity}
										</Text>
									</td>
									{!checkRole([ROLES.STAFF]) && (
										<td className={classes.tdStyle}>
											<Text
												variant="body2"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{row.priceTTC}
											</Text>
										</td>
									)}
									<td className={classes.tdActionsStyle}>
										<Flex gap={'10px'}>
											<ActionIcon
												data-testid={
													'W2FtGXR7_Z5jdHi6B--btn-download'
												}
												disabled={
													row.status !==
													QueueStatus.SUCCESS
												}
												radius={'lg'}
												size={'md'}
												variant="light"
												color="var(--mantine-color-igluBlue-9)"
												onClick={() => {
													onDownload(row);
												}}
											>
												<IconFileDownload
													className={
														classes.mintInformartionTokenId
													}
													size={'15px'}
												/>
											</ActionIcon>
											<ActionIcon
												data-testid={'btn-send'}
												disabled={
													row.status !==
													QueueStatus.SUCCESS
												}
												radius={'lg'}
												size={'md'}
												variant="light"
												color="var(--mantine-color-igluYellow-9)"
												onClick={() => {
													onSend(row);
												}}
											>
												<IconSend
													className={
														classes.mintInformartionTokenId
													}
													size={'15px'}
												/>
											</ActionIcon>
										</Flex>
									</td>
								</tr>
							))}
					</tbody>
				</table>
			</Box>
			{data &&
				Array.isArray(data) &&
				data.length == 0 && (
					<Box
						className={classes.containerTextsNoRecords}
					>
						<Image
							src={NoRecordsIcon}
							className={classes.iconNoRecords}
						/>
						<Text
							variant="caption"
							c="var(--mantine-color-igluTextSecondary-9)"
						>
							{t('global.datatable.noResultsFound')}
						</Text>
					</Box>
				)}
		</Box>
	);
};
